/**
 * useCRM Hook — Supabase-Anbindung für CRM-Modul
 * Tabellen: crm_kontakte, crm_pipeline_stages, crm_vorgaenge, crm_aufgaben, crm_email_vorlagen, crm_kommunikation
 * CRUD: Anlegen, Bearbeiten, Löschen, Suchen
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

// ============================================================
// TYPES
// ============================================================
export interface CRMKontakt {
  id: string;
  name: string;
  firma?: string;
  kategorie: string;
  email?: string;
  telefon?: string;
  mobil?: string;
  adresse?: string;
  plz?: string;
  ort?: string;
  notizen?: string;
  favorit: boolean;
  tags: string[];
  letzter_kontakt?: string;
  erstellt_am: string;
  aktualisiert_am: string;
  quelle?: string;
  // Erweiterte Felder (HubSpot-Style)
  firmen_ids?: string[];     // Zuordnung zu mehreren Firmen
  position?: string;         // Position/Titel in der Firma
  webseite?: string;
  linkedin?: string;
  geburtstag?: string;
  sprache?: string;
  status?: string;           // aktiv, inaktiv, lead, kunde
  bewertung?: number;        // 1-5 Sterne
  umsatz_gesamt?: number;
  letzte_aktivitaet?: string;
  betrieb?: string;          // gutachten, mietwagen, autoglas, werkstatt, protex, unfallgiganten
  kontakttyp?: string;
}

export interface CRMFirma {
  id: string;
  name: string;
  branche?: string;
  kategorie: string;         // werkstatt, versicherung, mietwagen, autoglas, anwalt, sonstige
  email?: string;
  telefon?: string;
  webseite?: string;
  adresse?: string;
  plz?: string;
  ort?: string;
  notizen?: string;
  logo_url?: string;
  anzahl_kontakte?: number;
  umsatz_gesamt?: number;
  erstellt_am: string;
  aktualisiert_am: string;
}

export interface CRMAktivitaet {
  id: string;
  kontakt_id?: string;
  firma_id?: string;
  typ: string;               // email, anruf, termin, notiz, akte, rechnung, dokument, marketing
  titel: string;
  beschreibung?: string;
  metadata?: Record<string, any>;  // Flexible Zusatzdaten (z.B. akte_id, rechnungs_nr, etc.)
  erstellt_von?: string;
  erstellt_am: string;
}

export interface CRMPipelineStage {
  id: string;
  name: string;
  farbe: string;
  position: number;
  aktiv: boolean;
}

export interface CRMVorgang {
  id: string;
  titel: string;
  kontakt_id?: string;
  stage_id?: string;
  stage_name: string;
  betrieb?: string;
  wert?: number;
  prioritaet: string;
  zustaendig?: string;
  faellig_am?: string;
  notizen?: string;
  erstellt_am: string;
  aktualisiert_am: string;
  abgeschlossen_am?: string;
  // Joined
  kontakt?: CRMKontakt;
}

export interface CRMAufgabe {
  id: string;
  titel: string;
  beschreibung?: string;
  kontakt_id?: string;
  vorgang_id?: string;
  typ: string;
  prioritaet: string;
  faellig_am: string;
  erledigt: boolean;
  erledigt_am?: string;
  erstellt_am: string;
  // Joined
  kontakt?: CRMKontakt;
}

export interface CRMEmailVorlage {
  id: string;
  name: string;
  kategorie: string;
  betreff: string;
  inhalt: string;
  variablen: string[];
  aktiv: boolean;
}

export interface CRMKommunikation {
  id: string;
  kontakt_id?: string;
  typ: string;
  richtung: string;
  betreff?: string;
  inhalt?: string;
  gmail_message_id?: string;
  absender_email?: string;
  empfaenger_email?: string;
  datum: string;
}

// ============================================================
// KONTAKTE HOOK
// ============================================================
export function useCRMKontakte() {
  const [kontakte, setKontakte] = useState<CRMKontakt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      // Supabase hat ein 1000-Zeilen-Limit pro Request
      // Wir laden alle Kontakte in Batches
      const allData: CRMKontakt[] = [];
      const pageSize = 1000;
      let from = 0;
      let hasMore = true;
      while (hasMore) {
        const { data, error: err } = await supabase
          .from('crm_kontakte')
          .select('*')
          .order('name')
          .range(from, from + pageSize - 1);
        if (err) throw err;
        if (data && data.length > 0) {
          allData.push(...data);
          from += pageSize;
          hasMore = data.length === pageSize;
        } else {
          hasMore = false;
        }
      }
      setKontakte(allData);
      setError(null);
    } catch (e: any) {
      console.error('CRM Kontakte fetch error:', e);
      setError(e.message);
      setKontakte([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (kontakt: Partial<CRMKontakt>) => {
    const { data, error: err } = await supabase
      .from('crm_kontakte')
      .insert(kontakt)
      .select()
      .single();
    if (err) throw err;
    setKontakte(prev => [...prev, data]);
    return data;
  }, []);

  const update = useCallback(async (id: string, updates: Partial<CRMKontakt>) => {
    const { data, error: err } = await supabase
      .from('crm_kontakte')
      .update({ ...updates, aktualisiert_am: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (err) throw err;
    setKontakte(prev => prev.map(k => k.id === id ? data : k));
    return data;
  }, []);

  const remove = useCallback(async (id: string) => {
    const { error: err } = await supabase.from('crm_kontakte').delete().eq('id', id);
    if (err) throw err;
    setKontakte(prev => prev.filter(k => k.id !== id));
  }, []);

  const toggleFavorit = useCallback(async (id: string) => {
    const kontakt = kontakte.find(k => k.id === id);
    if (!kontakt) return;
    await update(id, { favorit: !kontakt.favorit });
  }, [kontakte, update]);

  // Duplikat-Erkennung: Sucht nach Kontakten mit gleicher E-Mail, Telefon oder Name+Firma
  const findDuplicates = useCallback(async (kontakt: CRMKontakt): Promise<CRMKontakt[]> => {
    const dupes: CRMKontakt[] = [];
    const seenIds = new Set<string>([kontakt.id]);

    // 1. E-Mail-Match
    if (kontakt.email?.trim()) {
      const { data } = await supabase
        .from('crm_kontakte')
        .select('*')
        .eq('email', kontakt.email.trim().toLowerCase())
        .neq('id', kontakt.id)
        .limit(10);
      data?.forEach(d => { if (!seenIds.has(d.id)) { seenIds.add(d.id); dupes.push(d); } });
    }

    // 2. Telefon-Match (normalisiert)
    const normalizePhone = (p: string) => p.replace(/[\s\-\/\(\)]/g, '');
    for (const field of ['telefon', 'mobil'] as const) {
      const phone = kontakt[field]?.trim();
      if (phone && phone.length >= 6) {
        const norm = normalizePhone(phone);
        // Suche in allen geladenen Kontakten (client-seitig, da Supabase keine Regex-Suche hat)
        kontakte.forEach(k => {
          if (seenIds.has(k.id)) return;
          const kPhone = k.telefon?.trim() || '';
          const kMobil = k.mobil?.trim() || '';
          if ((kPhone && normalizePhone(kPhone) === norm) || (kMobil && normalizePhone(kMobil) === norm)) {
            seenIds.add(k.id);
            dupes.push(k);
          }
        });
      }
    }

    // 3. Name+Firma-Match (exakt)
    if (kontakt.name?.trim()) {
      const nameLower = kontakt.name.trim().toLowerCase();
      const firmaLower = (kontakt.firma || '').trim().toLowerCase();
      kontakte.forEach(k => {
        if (seenIds.has(k.id)) return;
        if (k.name?.trim().toLowerCase() === nameLower && (k.firma || '').trim().toLowerCase() === firmaLower) {
          seenIds.add(k.id);
          dupes.push(k);
        }
      });
    }

    return dupes;
  }, [kontakte]);

  // Kontakte zusammenführen: primary behält alle Daten, secondary wird gelöscht
  const mergeKontakte = useCallback(async (primaryId: string, secondaryIds: string[]) => {
    const primary = kontakte.find(k => k.id === primaryId);
    if (!primary) throw new Error('Primärer Kontakt nicht gefunden');

    const secondaries = kontakte.filter(k => secondaryIds.includes(k.id));
    if (secondaries.length === 0) throw new Error('Keine sekundären Kontakte gefunden');

    // Merge: Felder des primary mit fehlenden Feldern der secondaries auffüllen
    const mergedUpdates: Partial<CRMKontakt> = {};
    const textFields: (keyof CRMKontakt)[] = ['email', 'telefon', 'mobil', 'adresse', 'plz', 'ort', 'firma', 'position', 'webseite', 'linkedin', 'geburtstag', 'sprache'];
    
    for (const field of textFields) {
      if (!primary[field] && secondaries.some(s => s[field])) {
        (mergedUpdates as any)[field] = secondaries.find(s => s[field])?.[field];
      }
    }

    // Merge tags
    const allTags = new Set(primary.tags || []);
    secondaries.forEach(s => (s.tags || []).forEach(t => allTags.add(t)));
    allTags.add('zusammengefuehrt');
    mergedUpdates.tags = Array.from(allTags);

    // Merge notizen
    const allNotes = [primary.notizen || '', ...secondaries.map(s => s.notizen || '')].filter(Boolean);
    if (allNotes.length > 1) {
      mergedUpdates.notizen = allNotes.join('\n---\n');
    }

    // Update primary
    await update(primaryId, mergedUpdates);

    // Move related records (kommunikation, aktivitaeten, vorgaenge) to primary
    for (const secId of secondaryIds) {
      await supabase.from('crm_kommunikation').update({ kontakt_id: primaryId }).eq('kontakt_id', secId);
      await supabase.from('crm_aktivitaeten').update({ kontakt_id: primaryId }).eq('kontakt_id', secId);
      await supabase.from('crm_vorgaenge').update({ kontakt_id: primaryId }).eq('kontakt_id', secId);
      await supabase.from('crm_aufgaben').update({ kontakt_id: primaryId }).eq('kontakt_id', secId);
    }

    // Delete secondaries
    for (const secId of secondaryIds) {
      await remove(secId);
    }

    return primary;
  }, [kontakte, update, remove]);

  return { kontakte, loading, error, fetch, create, update, remove, toggleFavorit, findDuplicates, mergeKontakte };
}

// ============================================================
// PIPELINE HOOK
// ============================================================
export function useCRMPipeline() {
  const [stages, setStages] = useState<CRMPipelineStage[]>([]);
  const [vorgaenge, setVorgaenge] = useState<CRMVorgang[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [stagesRes, vorgaengeRes] = await Promise.all([
        supabase.from('crm_pipeline_stages').select('*').order('position'),
        supabase.from('crm_vorgaenge').select('*, kontakt:crm_kontakte(id, name, firma, email)').order('erstellt_am', { ascending: false }),
      ]);
      if (stagesRes.error) throw stagesRes.error;
      if (vorgaengeRes.error) throw vorgaengeRes.error;
      setStages(stagesRes.data || []);
      setVorgaenge(vorgaengeRes.data || []);
    } catch (e: any) {
      console.error('CRM Pipeline fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const createVorgang = useCallback(async (vorgang: Partial<CRMVorgang>) => {
    const { data, error: err } = await supabase
      .from('crm_vorgaenge')
      .insert(vorgang)
      .select('*, kontakt:crm_kontakte(id, name, firma, email)')
      .single();
    if (err) throw err;
    setVorgaenge(prev => [data, ...prev]);
    return data;
  }, []);

  const updateVorgang = useCallback(async (id: string, updates: Partial<CRMVorgang>) => {
    const { data, error: err } = await supabase
      .from('crm_vorgaenge')
      .update({ ...updates, aktualisiert_am: new Date().toISOString() })
      .eq('id', id)
      .select('*, kontakt:crm_kontakte(id, name, firma, email)')
      .single();
    if (err) throw err;
    setVorgaenge(prev => prev.map(v => v.id === id ? data : v));
    return data;
  }, []);

  const moveVorgang = useCallback(async (id: string, newStageName: string) => {
    const stage = stages.find(s => s.name.toLowerCase().replace(/\s+/g, '_') === newStageName || s.name === newStageName);
    await updateVorgang(id, {
      stage_name: newStageName,
      stage_id: stage?.id,
      ...(newStageName === 'abgeschlossen' ? { abgeschlossen_am: new Date().toISOString() } : {}),
    });
  }, [stages, updateVorgang]);

  const removeVorgang = useCallback(async (id: string) => {
    const { error: err } = await supabase.from('crm_vorgaenge').delete().eq('id', id);
    if (err) throw err;
    setVorgaenge(prev => prev.filter(v => v.id !== id));
  }, []);

  return { stages, vorgaenge, loading, fetch, createVorgang, updateVorgang, moveVorgang, removeVorgang };
}

// ============================================================
// AUFGABEN HOOK
// ============================================================
export function useCRMAufgaben() {
  const [aufgaben, setAufgaben] = useState<CRMAufgabe[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('crm_aufgaben')
        .select('*, kontakt:crm_kontakte(id, name, email)')
        .order('faellig_am');
      if (err) throw err;
      setAufgaben(data || []);
    } catch (e: any) {
      console.error('CRM Aufgaben fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (aufgabe: Partial<CRMAufgabe>) => {
    const { data, error: err } = await supabase
      .from('crm_aufgaben')
      .insert(aufgabe)
      .select('*, kontakt:crm_kontakte(id, name, email)')
      .single();
    if (err) throw err;
    setAufgaben(prev => [...prev, data]);
    return data;
  }, []);

  const update = useCallback(async (id: string, updates: Partial<CRMAufgabe>) => {
    const { data, error: err } = await supabase
      .from('crm_aufgaben')
      .update(updates)
      .eq('id', id)
      .select('*, kontakt:crm_kontakte(id, name, email)')
      .single();
    if (err) throw err;
    setAufgaben(prev => prev.map(a => a.id === id ? data : a));
    return data;
  }, []);

  const toggleErledigt = useCallback(async (id: string) => {
    const aufgabe = aufgaben.find(a => a.id === id);
    if (!aufgabe) return;
    await update(id, {
      erledigt: !aufgabe.erledigt,
      erledigt_am: !aufgabe.erledigt ? new Date().toISOString() : undefined,
    } as any);
  }, [aufgaben, update]);

  const remove = useCallback(async (id: string) => {
    const { error: err } = await supabase.from('crm_aufgaben').delete().eq('id', id);
    if (err) throw err;
    setAufgaben(prev => prev.filter(a => a.id !== id));
  }, []);

  return { aufgaben, loading, fetch, create, update, toggleErledigt, remove };
}

// ============================================================
// EMAIL VORLAGEN HOOK
// ============================================================
export function useCRMEmailVorlagen() {
  const [vorlagen, setVorlagen] = useState<CRMEmailVorlage[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('crm_email_vorlagen')
        .select('*')
        .order('name');
      if (err) throw err;
      setVorlagen(data || []);
    } catch (e: any) {
      console.error('CRM Email Vorlagen fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (vorlage: Partial<CRMEmailVorlage>) => {
    const { data, error: err } = await supabase
      .from('crm_email_vorlagen')
      .insert(vorlage)
      .select()
      .single();
    if (err) throw err;
    setVorlagen(prev => [...prev, data]);
    return data;
  }, []);

  const update = useCallback(async (id: string, updates: Partial<CRMEmailVorlage>) => {
    const { data, error: err } = await supabase
      .from('crm_email_vorlagen')
      .update({ ...updates, aktualisiert_am: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (err) throw err;
    setVorlagen(prev => prev.map(v => v.id === id ? data : v));
    return data;
  }, []);

  return { vorlagen, loading, fetch, create, update };
}

// ============================================================
// KOMMUNIKATION HOOK (E-Mail-Zuordnung)
// ============================================================
export function useCRMKommunikation(kontaktId?: string) {
  const [kommunikation, setKommunikation] = useState<CRMKommunikation[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase.from('crm_kommunikation').select('*').order('datum', { ascending: false });
      if (kontaktId) query = query.eq('kontakt_id', kontaktId);
      const { data, error: err } = await query.limit(100);
      if (err) throw err;
      setKommunikation(data || []);
    } catch (e: any) {
      console.error('CRM Kommunikation fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [kontaktId]);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (entry: Partial<CRMKommunikation>) => {
    const { data, error: err } = await supabase
      .from('crm_kommunikation')
      .insert(entry)
      .select()
      .single();
    if (err) throw err;
    setKommunikation(prev => [data, ...prev]);
    return data;
  }, []);

  // Match incoming email to contact by email address
  const matchEmailToKontakt = useCallback(async (absenderEmail: string): Promise<CRMKontakt | null> => {
    const { data } = await supabase
      .from('crm_kontakte')
      .select('*')
      .eq('email', absenderEmail)
      .limit(1)
      .single();
    return data;
  }, []);

  // Sync Gmail messages to CRM kommunikation
  const syncGmailMessages = useCallback(async (messages: Array<{ id: string; from: string; subject: string; snippet: string; date: string }>) => {
    const newEntries: Partial<CRMKommunikation>[] = [];
    for (const msg of messages) {
      // Check if already synced
      const { data: existing } = await supabase
        .from('crm_kommunikation')
        .select('id')
        .eq('gmail_message_id', msg.id)
        .limit(1);
      if (existing && existing.length > 0) continue;

      // Find matching contact
      const kontakt = await matchEmailToKontakt(msg.from);
      newEntries.push({
        kontakt_id: kontakt?.id,
        typ: 'email',
        richtung: 'eingehend',
        betreff: msg.subject,
        inhalt: msg.snippet,
        gmail_message_id: msg.id,
        absender_email: msg.from,
        datum: msg.date,
      });
    }
    if (newEntries.length > 0) {
      const { data, error: err } = await supabase
        .from('crm_kommunikation')
        .insert(newEntries)
        .select();
      if (err) throw err;
      setKommunikation(prev => [...(data || []), ...prev]);
      return data;
    }
    return [];
  }, [matchEmailToKontakt]);

  return { kommunikation, loading, fetch, create, matchEmailToKontakt, syncGmailMessages };
}

// ============================================================
// FIRMEN HOOK (HubSpot-Style Companies)
// ============================================================
export function useCRMFirmen() {
  const [firmen, setFirmen] = useState<CRMFirma[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('crm_firmen')
        .select('*')
        .order('name');
      if (err) throw err;
      setFirmen(data || []);
      setError(null);
    } catch (e: any) {
      console.error('CRM Firmen fetch error:', e);
      setError(e.message);
      setFirmen([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (firma: Partial<CRMFirma>) => {
    const { data, error: err } = await supabase
      .from('crm_firmen')
      .insert(firma)
      .select()
      .single();
    if (err) throw err;
    setFirmen(prev => [...prev, data]);
    return data;
  }, []);

  const update = useCallback(async (id: string, updates: Partial<CRMFirma>) => {
    const { data, error: err } = await supabase
      .from('crm_firmen')
      .update({ ...updates, aktualisiert_am: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (err) throw err;
    setFirmen(prev => prev.map(f => f.id === id ? data : f));
    return data;
  }, []);

  const remove = useCallback(async (id: string) => {
    const { error: err } = await supabase.from('crm_firmen').delete().eq('id', id);
    if (err) throw err;
    setFirmen(prev => prev.filter(f => f.id !== id));
  }, []);

  return { firmen, loading, error, fetch, create, update, remove };
}

// ============================================================
// AKTIVITÄTEN HOOK (Timeline / Activity Feed)
// ============================================================
export function useCRMAktivitaeten(kontaktId?: string, firmaId?: string) {
  const [aktivitaeten, setAktivitaeten] = useState<CRMAktivitaet[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase.from('crm_aktivitaeten').select('*').order('erstellt_am', { ascending: false });
      if (kontaktId) query = query.eq('kontakt_id', kontaktId);
      if (firmaId) query = query.eq('firma_id', firmaId);
      const { data, error: err } = await query.limit(200);
      if (err) throw err;
      setAktivitaeten(data || []);
    } catch (e: any) {
      console.error('CRM Aktivitäten fetch error:', e);
      setAktivitaeten([]);
    } finally {
      setLoading(false);
    }
  }, [kontaktId, firmaId]);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (entry: Partial<CRMAktivitaet>) => {
    const { data, error: err } = await supabase
      .from('crm_aktivitaeten')
      .insert(entry)
      .select()
      .single();
    if (err) throw err;
    setAktivitaeten(prev => [data, ...prev]);
    return data;
  }, []);

  return { aktivitaeten, loading, fetch, create };
}

// ============================================================
// BETEILIGUNGEN HOOK (Fall-Übersicht pro Kontakt, Issue #337)
// ============================================================
export interface CRMKontaktBeteiligung {
  kontakt_id: string;
  akte_id: string;
  akten_nr: string | null;
  akten_nummer: string | null;
  titel: string | null;
  business_unit: string;
  status: string | null;
  rolle: string;
  sortier_datum: string | null;
  quelle: string;
}

export function useCRMKontaktBeteiligungen(kontaktId?: string) {
  const [beteiligungen, setBeteiligungen] = useState<CRMKontaktBeteiligung[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!kontaktId) {
      setBeteiligungen([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('crm_kontakt_beteiligungen')
        .select('*')
        .eq('kontakt_id', kontaktId)
        .order('sortier_datum', { ascending: false })
        .limit(500);
      if (err) throw err;
      // Dedupe identical (kontakt_id, akte_id, rolle) tuples — mehrere Quellen
      // können denselben Treffer liefern (z. B. akten_beteiligte + FK-Feld).
      const seen = new Set<string>();
      const deduped: CRMKontaktBeteiligung[] = [];
      for (const row of (data as CRMKontaktBeteiligung[]) || []) {
        const key = `${row.akte_id}|${row.rolle}`;
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(row);
      }
      setBeteiligungen(deduped);
      setError(null);
    } catch (e: any) {
      console.error('CRM Beteiligungen fetch error:', e);
      setError(e.message);
      setBeteiligungen([]);
    } finally {
      setLoading(false);
    }
  }, [kontaktId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { beteiligungen, loading, error, fetch };
}

// ============================================================
// KONTAKT-SUCHE HOOK (für Autovervollständigung im E-Mail-Compose)
// ============================================================
export function useCRMKontaktSuche() {
  const search = useCallback(async (query: string): Promise<CRMKontakt[]> => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    const { data, error: err } = await supabase
      .from('crm_kontakte')
      .select('*')
      .or(`name.ilike.%${q}%,email.ilike.%${q}%,firma.ilike.%${q}%`)
      .limit(10);
    if (err) {
      console.error('Kontakt-Suche Fehler:', err);
      return [];
    }
    return data || [];
  }, []);

  return { search };
}

// ============================================================
// AKTE-BETEILIGTE HOOK — verknüpfte CRM-Kontakte einer Akte (Issue #499)
// ============================================================
export interface AkteBeteiligter {
  id: string;
  akte_id: string;
  kontakt_id: string;
  rolle: string;
  notizen: string | null;
  created_at: string;
  kontakt?: CRMKontakt;
}

export function useAkteBeteiligte(akteId?: string) {
  const [beteiligte, setBeteiligte] = useState<AkteBeteiligter[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!akteId) {
      setBeteiligte([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('akten_beteiligte')
        .select('*, kontakt:crm_kontakte(*)')
        .eq('akte_id', akteId)
        .order('created_at', { ascending: true });
      if (err) throw err;
      setBeteiligte((data as AkteBeteiligter[]) || []);
      setError(null);
    } catch (e: any) {
      console.error('useAkteBeteiligte fetch error:', e);
      setError(e.message);
      setBeteiligte([]);
    } finally {
      setLoading(false);
    }
  }, [akteId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { beteiligte, loading, error, fetch };
}

// ============================================================
// LIGHTWEIGHT CREATE-ONLY HOOK — no bulk fetch (Issue #641)
// Use on pages that already load contacts via cursor-paging API.
// ============================================================
export function useCreateKontakt() {
  const create = useCallback(async (kontakt: Partial<CRMKontakt>) => {
    const { data, error: err } = await supabase
      .from('crm_kontakte')
      .insert(kontakt)
      .select()
      .single();
    if (err) throw err;
    return data as CRMKontakt;
  }, []);

  return { create };
}
